import { useCallback, useMemo } from 'react'
import { IRootReducerState } from 'state/rootReducer'
import { useSelector } from 'react-redux'
import { isPortalSelector, useIsPortal } from './PermissionSelector'
import { generatePath } from 'react-router-dom'
import { IOrder } from '@cbgms/api'
import { OrderQuotationDetailUrl, OrderQuotationsUrl } from 'app/order/RouteConstants'
import { WorkOrderQuotationDetailUrl, WorkOrderQuotationsUrl } from 'app/workorder/RouteConstants'

const getBaseRoute = (isPortal: boolean): string => (isPortal ? '/orders/' : '/workorders/')
// workorders or portal orders
export const workorderBaseRouteSelector = (state: IRootReducerState) => {
  return getBaseRoute(isPortalSelector(state))
}

// useWorkorderBaseRoute should be used to determine the url to navigate to, order | workorder
export const useWorkorderBaseRoute = () => {
  const isPortal = useIsPortal()
  return useMemo(() => getBaseRoute(isPortal), [isPortal])
}

export const useWorkorderDetailsBaseRoute = (uuid: string) => {
  const baseRoute = useWorkorderBaseRoute()
  return useMemo(() => `${baseRoute}${uuid}`, [baseRoute, uuid])
}

// workorders or portal orders
export const newWorkorderSuffixRouteSelector = (state: IRootReducerState) => {
  return isPortalSelector(state) ? '' : '/quick-create'
}
export const useNewWorkorderBaseRouteFunc = () => {
  const baseRoute = useSelector(workorderBaseRouteSelector)
  const suffix = useSelector(newWorkorderSuffixRouteSelector)
  return useCallback(
    (uuid: string) => {
      return `${baseRoute}${uuid}${suffix}`
    },
    [baseRoute, suffix]
  )
}

export const archiveOrderHistoryBaseRouteBuilder = (isPortal: boolean, order: IOrder) => {
  return isPortal
    ? generatePath('/archive/:WorkorderUUID/wholesalers', { WorkorderUUID: order.WorkorderUUID })
    : generatePath('/history/orders/:OrderUUID', { OrderUUID: order.UUID })
}
// Order archive
export const archiveOrderHistoryBaseRouteSelector = (state: IRootReducerState, order: IOrder) => {
  return archiveOrderHistoryBaseRouteBuilder(isPortalSelector(state), order)
}
// useWorkorderBaseRoute should be used to determine the url to navigate to, order | workorder
export const useArchiveOrderHistoryBaseRouteSelector = (order: IOrder) => useSelector(s => archiveOrderHistoryBaseRouteSelector(s, order))

// Quotations
export const useQuotationsBaseRoute = () => {
  const isPortal = useIsPortal()
  return generatePath(isPortal ? OrderQuotationsUrl : WorkOrderQuotationsUrl)
}

export const useQuotationsDetailsBaseRoute = () => {
  const isPortal = useIsPortal()
  const baseRoute = isPortal ? OrderQuotationDetailUrl : WorkOrderQuotationDetailUrl
  return useCallback(
    (uuid: string) => {
      return generatePath(baseRoute, { QuotationUUID: uuid })
    },
    [baseRoute]
  )
}
